import { useMemo } from "react"
import { BarStack } from "@visx/shape"
import { Group } from "@visx/group"

import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale"

import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip"

import { localPoint } from "@visx/event"
import { useI18n } from "providers/I18n"

import { useRevenueParams } from "../../../hooks/useRevenueParams"
import { formatData, getTitles, getRegionTitle, sumDirection } from "./utils"
import TooltipContent from "./TooltipContent"
import YAxis from "./YAxis"

import XAxis from "./XAxis"
import { getPalette } from "../palette"
import { netFormat, percentanceFormat } from "../../../helpers/formatters"
import { GridRows } from "@visx/grid"
import XAxisSub from "./XAxisSub"
import PosValuesAxis from "./PosValuesAxis"
import GlobalsAxis from "./GlobalsAxis"
import TBGBarChart from "./TBGBarChart"
import { FirstBarInfos, SecondBarInfos } from "../../DetailsView/NonGCFView"
import Pill from "./Pill"

export type TooltipData = {
  bar: {
    key: string
    index: number
    height: number
    width: number
    x: number
    y: number
    color: string
  }
  hoveredIndex: number
  showSecondBar?: boolean
}

export type BarStackProps = {
  width: number
  height: number
  search: string
  entity: string
  tableData: any
  firstBarInfos: FirstBarInfos
  secondBarInfos?: SecondBarInfos
  dataType: "gcf" | "non-gcf"
  availableEntities: string[][]
  showSecondBar?: any
  entityIndex: number
  isExpanded?: boolean
}

const tooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: "var(--primary-main-dark-green)",
  color: "white",
  zIndex: 9999999,
  border: "2px solid var(--primary-main-dark-green)",
  borderRadius: "10px",
}

export default function BarChart({
  width,
  height,
  search,
  entity,
  tableData,
  firstBarInfos,
  secondBarInfos,
  dataType,
  availableEntities,
  entityIndex,
  showSecondBar,
  isExpanded,
}: BarStackProps) {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>()

  const { t } = useI18n()
  const { regionParam } = useRevenueParams()
  const globalEntityName = tableData?.total?.[entity]?.entityName

  const commonProps = useMemo(() => {
    return {
      data: tableData,
      search,
      entity,
      regionParam,
      t,
      availableEntities,
      entityIndex,
    }
  }, [
    entity,
    regionParam,
    search,
    t,
    tableData,
    availableEntities,
    entityIndex,
  ])

  const hasAvailableEntities = !!availableEntities[entityIndex].length

  const formatedTotalRevenueData = useMemo(() => {
    return firstBarInfos.revenue.map((key) =>
      formatData(key, { ...commonProps, isGlobal: true }, dataType),
    )
  }, [commonProps, dataType, firstBarInfos.revenue])

  const formatedTotalTbgRevenueData = useMemo(() => {
    return secondBarInfos?.tbgNet?.map((key) =>
      formatData(key, { ...commonProps, isGlobal: true }, dataType),
    )
  }, [commonProps, dataType, secondBarInfos?.tbgNet])

  const revenueGlobals = formatedTotalRevenueData.map(getTitles)
  const tbgRevenueGlobals = formatedTotalTbgRevenueData?.map(getTitles)

  const formatedNetData = useMemo(() => {
    return firstBarInfos.revenue.map((key) =>
      formatData(key, commonProps, dataType),
    )
  }, [commonProps, dataType, firstBarInfos.revenue])

  const formatedOIData = useMemo(() => {
    return firstBarInfos.oibiaPercent.map((key) =>
      formatData(key, commonProps, dataType),
    )
  }, [commonProps, dataType, firstBarInfos.oibiaPercent])

  const formatedTbgNetData = useMemo(() => {
    return secondBarInfos?.tbgNet?.map((key) =>
      formatData(key, commonProps, dataType, "tbg"),
    )
  }, [commonProps, dataType, secondBarInfos?.tbgNet])

  const keys = Object.keys(formatedNetData[0]).filter((d) => d !== "title")

  const updatedKeys = [getRegionTitle(entity, regionParam)].concat(keys)

  const defaultConfig = {
    top: 50,
    left: 75,
    doubleBarPosition: 3,
  }

  const totals = formatedNetData.reduce((ret, cur) => {
    const pos = sumDirection(keys, cur, 1)
    const neg = sumDirection(keys, cur, -1)
    ret.push({ pos, neg })
    return ret
  }, [])

  const tbgTotals = formatedTbgNetData?.reduce((ret, cur) => {
    const pos = sumDirection(keys, cur, 1)
    const neg = sumDirection(keys, cur, -1)
    ret.push({ pos, neg })
    return ret
  }, [])

  const minTotals = totals.map((t: { pos: number; neg: number }) => t.neg)
  const minTBGTotals = useMemo(() => {
    return showSecondBar
      ? tbgTotals.map((t: { pos: number; neg: number }) => t.neg)
      : []
  }, [showSecondBar, tbgTotals])

  const maxTotals = totals.map((t: { pos: number; neg: number }) => t.pos)

  const maxTBGTotals = useMemo(() => {
    return showSecondBar
      ? tbgTotals.map((t: { pos: number; neg: number }) => t.pos)
      : []
  }, [showSecondBar, tbgTotals])

  const YScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [
          showSecondBar
            ? Math.min(...minTBGTotals, ...minTotals)
            : Math.min(...minTotals),
          showSecondBar
            ? Math.max(...maxTBGTotals, ...maxTotals)
            : Math.max(...maxTotals),
        ],

        range: [height / 1.3, 0],
      }),
    [showSecondBar, minTBGTotals, minTotals, maxTBGTotals, maxTotals, height],
  )

  const topPosRevenueValuesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: [
          `${maxTotals[0] === 0 ? "" : netFormat(maxTotals[0])}-1`,
          `${maxTotals[1] === 0 ? "" : netFormat(maxTotals[1])}-2`,
          `${maxTotals[2] === 0 ? "" : netFormat(maxTotals[2])}-3`,
        ],
        padding: showSecondBar ? 0.6 : 0.45,
        range: [defaultConfig.left, width],
      }),
    [maxTotals, showSecondBar, defaultConfig.left, width],
  )

  const globalsRevenueScale = useMemo(
    () =>
      scaleBand<string>({
        domain: [
          `${netFormat(revenueGlobals[0])}-1`,
          `${netFormat(revenueGlobals[1])}-2`,
          `${netFormat(revenueGlobals[2])}-3`,
        ],
        padding: showSecondBar ? 0.6 : 0.45,
        range: [defaultConfig.left, width],
      }),
    [revenueGlobals, showSecondBar, defaultConfig.left, width],
  )

  const bottomTitlesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: formatedNetData.map(getTitles),
        padding: showSecondBar ? 0.6 : 0.45,
        range: [defaultConfig.left, width],
      }),
    [defaultConfig.left, formatedNetData, width, showSecondBar],
  )

  const bottomSubRevenueTitlesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: [
          `${t("revenue.revenue")}-1`,
          `${t("revenue.revenue")}-2`,
          `${t("revenue.revenue")}-3`,
        ],
        padding: showSecondBar ? 0.6 : 0.45,
        range: [defaultConfig.left, width],
      }),
    [t, showSecondBar, defaultConfig.left, width],
  )

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: updatedKeys,
        range: getPalette(entity),
      }),
    [entity, updatedKeys],
  )

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  })

  return (
    <>
      <svg ref={containerRef} width={width} height={height}>
        <GridRows
          scale={YScale}
          numTicks={5}
          width={width}
          top={defaultConfig.top}
          left={defaultConfig.left}
        >
          {(lines) => {
            return lines.lines.map((line, index) => (
              <line
                key={index}
                x1="0"
                x2={width}
                y1={line.from.y}
                y2={line.to.y}
                stroke={line.from.y! > YScale(0) ? "var(--other-red)" : "grey"}
                strokeWidth={line.from.y! === YScale(0) ? 1 : 0.5}
                strokeLinecap="round"
              />
            ))
          }}
        </GridRows>
        <Group>
          <BarStack
            key={entityIndex}
            data={formatedNetData}
            keys={keys}
            x={getTitles}
            xScale={bottomTitlesScale}
            yScale={YScale}
            color={colorScale}
            offset="diverging"
          >
            {(barStacks) => {
              return barStacks.map((barStack, i) => {
                return (
                  <g key={i}>
                    {barStack.bars.map((bar, index) => {
                      const calculatedX =
                        bar.x +
                        (showSecondBar
                          ? -defaultConfig.doubleBarPosition - bar.width / 2
                          : 0)
                      const formatedOIValue = percentanceFormat(
                        formatedOIData[bar.index][bar.key],
                      )
                      return (
                        <g>
                          <rect
                            key={`bar-stack-${barStack.index}-${index}`}
                            x={calculatedX}
                            y={bar.y + defaultConfig.top}
                            height={bar.height}
                            width={bar.width}
                            fill={getPalette(entity)[barStack.index]}
                            opacity={1}
                            onMouseLeave={() => hideTooltip()}
                            onMouseMove={(event) => {
                              const eventSvgCoords = localPoint(event)
                              const left = bar.x + bar.width / 2
                              showTooltip({
                                tooltipData: { bar, hoveredIndex: index },
                                tooltipTop: eventSvgCoords?.y,
                                tooltipLeft: left,
                              })
                            }}
                          />
                          {isExpanded && bar.height > 30 && (
                            <Pill
                              calculatedX={calculatedX}
                              barstackIndex={barStack.index}
                              bar={bar}
                              top={defaultConfig.top}
                              formatedOIData={formatedOIData}
                              formatedOIValue={formatedOIValue}
                              dataType={dataType}
                              showSecondBar={showSecondBar}
                            />
                          )}
                        </g>
                      )
                    })}
                  </g>
                )
              })
            }}
          </BarStack>
          {showSecondBar && (
            <TBGBarChart
              YScale={YScale}
              availableEntities={availableEntities}
              dataType={dataType}
              defaultConfig={defaultConfig}
              entity={entity}
              entityIndex={entityIndex}
              height={height}
              search={search}
              tableData={tableData}
              tbgNetKeys={secondBarInfos?.tbgNet!}
              tbgOibiaKeys={secondBarInfos?.tbgPercent!}
              width={width}
              isExpanded={isExpanded}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              showSecondBar={showSecondBar}
              hasAvailableEntities={hasAvailableEntities}
            />
          )}
        </Group>
        {hasAvailableEntities && (
          <XAxis scale={bottomTitlesScale} top={height / 1.15} />
        )}
        <PosValuesAxis
          scale={topPosRevenueValuesScale}
          top={[
            YScale(maxTotals[0]),
            YScale(maxTotals[1]),
            YScale(maxTotals[2]),
          ]}
          x={showSecondBar ? -18.5 : 0}
          chartWidth={width}
        />
        {hasAvailableEntities && (
          <GlobalsAxis
            scale={globalsRevenueScale}
            top={[
              YScale(maxTotals[0]),
              YScale(maxTotals[1]),
              YScale(maxTotals[2]),
            ]}
            x={showSecondBar ? -18.5 : 0}
            chartWidth={width}
          />
        )}
        {hasAvailableEntities && (
          <XAxisSub
            scale={bottomSubRevenueTitlesScale}
            top={isExpanded ? height / 1.195 : height / 1.185}
            x={showSecondBar ? -18.5 : 0}
            chartWidth={width}
          />
        )}
        <YAxis scale={YScale} />
      </svg>

      {tooltipOpen &&
        tooltipData &&
        tooltipData.bar.key !== "Global" &&
        tooltipData.bar.key !== "Total" && (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            style={tooltipStyles}
          >
            <TooltipContent
              data={tooltipData}
              commonProps={commonProps}
              totalData={
                tooltipData.showSecondBar
                  ? formatedTotalTbgRevenueData
                  : formatedTotalRevenueData
              }
              firstBarOibiaNet={firstBarInfos.oibiaNet}
              firstBarOibiaPercent={firstBarInfos.oibiaPercent}
              secondBarTbgPercent={secondBarInfos?.tbgPercent || []}
              totalsArray={
                tooltipData.showSecondBar
                  ? tbgRevenueGlobals || []
                  : revenueGlobals
              }
              dataType={dataType}
              showSecondBar={tooltipData.showSecondBar}
              globalEntityName={globalEntityName}
            />
          </TooltipInPortal>
        )}
    </>
  )
}
